/**
 * The pairing shapes both the operator and the bakery house read and write
 * (docs/plans/device-pairing.html 2.3).
 *
 * Stated here once, and imported by `internal/index.ts` and `partner/index.ts`,
 * because the two mounts serve the SAME dialog to two audiences: an operator on
 * `/internal/bakery-locations/:locationId/...` and a house admin on
 * `/c/locations/:locationId/...` with `settings.write`. The paths differ; the
 * shapes must not. Every earlier pair of mounts that restated a shape drifted -
 * `BakerySettings` is stated once and reused by `/c` for exactly this reason.
 *
 * What is deliberately NOT here: the PIN itself. `LocationPairing` answers what
 * STATE the PIN is in, when it was set and by which device, and never the four
 * digits - those leave the platform only through `sendDevicePin`, decrypted by
 * the notification consumer at send time, so they never sit in an outbox row, a
 * dead letter or the dev notification mirror.
 */

import { z } from 'zod';
import { DeviceKind, DevicePin, Instant, Uuid } from './common';
import { HandheldTelemetry } from './handheld-telemetry';
import { CounterReadinessSchema } from './projections/counter-readiness';

/** One paired device as the Devices list shows it (plan 2.1, BAK-037). */
export const DeviceRow = z.object({
  id: Uuid,
  label: z.string().min(1).max(60),
  /**
   * The identity the DEVICE presents at pairing, and the one a human matches
   * against the counter's own Appareil screen: a Sunmi sends the system's
   * device id (`ANDROID_ID`, kept by the device until a factory reset - NOT
   * `Build.SERIAL`, which two V2 units were measured sharing), so the same
   * handheld is the same id however many times it is reinstalled or
   * re-paired, and the (shop, deviceId) upsert is what keeps it one row. `id`
   * above is the platform's row id and means nothing to anybody standing at a
   * counter.
   */
  deviceId: z.string().min(1).max(128),
  /**
   * Null until a device has reported what it is: `pairDevice` may be called
   * without a `kind`, and the rows adopted while the retired PIN-session path
   * was being wound down carry none. The next heartbeat fills it in.
   */
  kind: DeviceKind.nullable(),
  appVersion: z.string().max(40).nullable(),
  pairedAt: Instant.nullable(),
  lastSeenAt: Instant.nullable(),
  /**
   * The last beat came from the handheld's periodic job with no app screen in
   * front (BAK-059, `tablet_devices.last_beat_screenless`): the cadence that
   * beat was sent at, and so how long a silence after it means nothing. The
   * dialog judges the unit on it, as the Devices list does, and says "In
   * background" beside Online (#2004, P78, P84). False for a unit never seen.
   */
  lastBeatScreenless: z.boolean(),
  /**
   * A removed device is DELETED (founder decision 2026-08-28), so every row
   * listed is active and `revokedAt` is null; both fields stay on the wire so
   * an older client parses the reply.
   */
  active: z.boolean(),
  revokedAt: Instant.nullable(),
  /**
   * What the device last said about itself (handheld-fleet plan 9), or null.
   *
   * Null for three different devices and the screen does not need to tell them
   * apart: a browser tablet, which reports none; a Sunmi on a build that
   * predates telemetry; and a row whose stored record no longer parses against
   * this schema, which is read as "nothing reported" rather than failing the
   * whole response - one device on an old build must never take a shop's
   * pairing dialog down with it.
   */
  telemetry: HandheldTelemetry.nullable(),
  /** When that record was written, which is the beat it came on. */
  telemetryAt: Instant.nullable(),
});
export type DeviceRow = z.infer<typeof DeviceRow>;

/** Where this shop's PIN stands. `setAt` and `setByDeviceId` are null unless SET. */
export const DevicePinStatus = z.object({
  state: z.enum(['NOT_SET', 'SET', 'RESET_PENDING']),
  setAt: Instant.nullable(),
  /**
   * The device that chose it, so the modal can say "code PIN choisi le 21 aout
   * sur Sunmi 1". A device, never a person, and never the digits.
   *
   * It is a `DeviceRow.id` - the row uuid - because that is the only identifier
   * the list beside it carries, and the sentence above is a JOIN between the
   * two. Left as a plain string rather than `Uuid` so a row written before
   * pairing shipped, holding whatever it holds, renders as an unmatched id
   * instead of failing the whole response.
   */
  setByDeviceId: z.string().max(128).nullable(),
});
export type DevicePinStatus = z.infer<typeof DevicePinStatus>;

/**
 * A counter locked out of its own PIN keypad right now (plan 2.1).
 *
 * The throttle is per LOCATION, not per device, because the PIN belongs to the
 * shop - so this is a fact about the counter and not about any one tablet, and
 * it is the operator's answer to the 06:10 call that says "it will not let us
 * in". `failures` is repeated inside it deliberately: a screen showing the
 * lockout shows both halves of the sentence, and reading one from here and the
 * other from `pinFailures` beside it is how the two come to disagree.
 */
export const DevicePinLockout = z.object({
  lockedUntil: Instant,
  failures: z.int().nonnegative(),
});
export type DevicePinLockout = z.infer<typeof DevicePinLockout>;

/**
 * What became of the last credential this shop was sent (TST-002).
 *
 * The dialog could say a code had been sent and never what happened next, so
 * "the baker never got it" was answerable only by an operator reading Twilio's
 * dashboard - and on a demo instance the answer was usually that the message
 * had been HELD by `NOTIFY_ALLOWLIST` and nobody could have received it. Read
 * off the notification ledger by the newest record whose idempotency key names
 * this shop, across both the pairing-code and the PIN sends.
 *
 * `messageRef` is the provider's own (`SM...` from Twilio) and is null unless
 * the message really left; `reason` carries why it was held or how it failed.
 */
export const PairingLastSend = z.object({
  channel: z.enum(['EMAIL', 'SMS']),
  at: Instant,
  status: z.enum(['SENT', 'HELD', 'FAILED']),
  messageRef: z.string().max(200).nullable(),
  reason: z.string().max(500).nullable(),
});
export type PairingLastSend = z.infer<typeof PairingLastSend>;

/** Everything the Pairing dialog shows for one shop. */
export const LocationPairing = z.object({
  /**
   * The 10 Crockford base32 characters, ungrouped. The SCREEN groups them
   * (`7K3M-Q2P9-XA`) and the client strips the dashes before sending them back:
   * one representation on the wire, one for human eyes.
   *
   * Null for a shop minted before pairing existed and not yet re-read - the
   * worker mints one on first read, so a null here is a state a screen shows
   * once and never again.
   */
  code: z.string().length(10).nullable(),
  codeIssuedAt: Instant.nullable(),
  /**
   * The `/pair/<code>` page on the bakery host, composed server-side from
   * PLATFORM_DOMAIN (OPS-018). Never assembled by a screen: a client that knows
   * how to build this URL is a client that has a host written down in it.
   */
  pageUrl: z.url(),
  pin: DevicePinStatus,
  /**
   * Consecutive wrong PINs at this counter since the last correct one, whether
   * or not they have added up to a lockout yet. Zero for a shop that has never
   * failed and zero for one that was just unlocked - the same record, which is
   * what makes "never" and "forgiven" indistinguishable on purpose.
   */
  pinFailures: z.int().nonnegative(),
  /** Null unless the counter is locked out RIGHT NOW; an expired lock is not one. */
  lockout: DevicePinLockout.nullable(),
  /** The last code or PIN sent to this shop, and its fate. Null if none ever was. */
  lastSend: PairingLastSend.nullable(),
  devices: z.array(DeviceRow),
  /**
   * THE word about the COUNTER this dialog is open on (OPS-035), derived
   * server-side by `counterReadinessOf` from the devices below, the last beat
   * any of them sent and whether the shop is paused.
   *
   * It is a fact about the SHOP, not about any one row: a house running a Sunmi
   * beside a browser tablet is reachable while either one is. The rows say
   * which unit is quiet; this says whether anybody at that address can be given
   * an order, which is the question a house admin opening this dialog at 06:10
   * is actually asking.
   */
  readiness: CounterReadinessSchema,
  /**
   * The platform's instant when this was read (F3): the dialog judges a
   * device's silence and the service morning on the PLATFORM clock
   * (`usePlatformNow`), so a rehearsal a week ahead reads the way the server
   * does rather than by the reader's wall clock.
   */
  serverTime: Instant,
});
export type LocationPairing = z.infer<typeof LocationPairing>;

/** What a regenerate answers: the new code, and nothing about any device. */
export const PairingCodeIssued = z.object({
  code: z.string().length(10),
  codeIssuedAt: Instant,
});
export type PairingCodeIssued = z.infer<typeof PairingCodeIssued>;

/**
 * Send the code, or send the PIN. One body for both, because they are one
 * decision made twice.
 *
 * `to` absent means "the contact this shop already has for that channel", and a
 * shop with none is answered 400 `no_contact` rather than silently doing
 * nothing. `to` present means send THERE and SAVE it as the contact in the same
 * transaction - which is how an email or a mobile gets onto a shop at all, from
 * the one dialog somebody already has open.
 *
 * The house side may also pass the literal `me`, which resolves to the
 * signed-in admin's own Clerk address. It is deliberately not an email in the
 * `to` field: an admin sending to themselves must not have to type an address
 * the platform already knows, and a typo there sends a PIN to a stranger.
 */
export const SendPairingMessage = z.object({
  channel: z.enum(['EMAIL', 'SMS']),
  to: z.union([z.literal('me'), z.string().min(3).max(200)]).optional(),
});
export type SendPairingMessage = z.infer<typeof SendPairingMessage>;

/**
 * Where it actually went, so the screen can say so. Unmasked: the operator
 * chose the destination a moment ago and needs to see it was the right one -
 * and the audit entry records the same value.
 */
export const PairingMessageSent = z.object({ sentTo: z.string().min(1).max(200) });
export type PairingMessageSent = z.infer<typeof PairingMessageSent>;

/** A reset is a MARKER, not a clear - see the plan's 2.4 for why. */
export const DevicePinReset = z.object({ resetAt: Instant });
export type DevicePinReset = z.infer<typeof DevicePinReset>;

/**
 * The operator forgave the lockout: this counter may type again now.
 *
 * The other half of "unblock a baker remotely". A reset asks the counter to
 * choose a NEW code, which is the wrong answer at 06:10 when the staff know
 * the code perfectly well and mistyped it five times with floured hands - this
 * is the answer to that call, and it changes no credential at all.
 */
export const DevicePinLockoutCleared = z.object({ clearedAt: Instant });
export type DevicePinLockoutCleared = z.infer<typeof DevicePinLockoutCleared>;

/**
 * The operator CHOOSES the PIN, rather than asking the counter to (plan 2.4).
 *
 * Unlike `resetDevicePin` this overwrites: a shop whose staff have all
 * forgotten the code, on the phone to support, gets a code they are told in
 * that call - and a reset would have left them at a keypad choosing one on a
 * device nobody standing there can operate.
 *
 * `channel` and `to` are the send, and they are the same two fields
 * `SendPairingMessage` carries and mean the same things: `to` absent sends to
 * the stored contact, `to` present sends there and SAVES it, and the house
 * side's literal `me` is the admin's own address. Absent `channel`, nothing is
 * sent and the operator reads the digits down the phone.
 */
export const SetDevicePinByAdmin = z.object({
  pin: DevicePin,
  channel: z.enum(['EMAIL', 'SMS']).optional(),
  to: SendPairingMessage.shape.to,
});
export type SetDevicePinByAdmin = z.infer<typeof SetDevicePinByAdmin>;

/** `sentTo` is null when no `channel` was asked for; unmasked, like a send. */
export const DevicePinSetByAdmin = z.object({
  pinSetAt: Instant,
  sentTo: z.string().min(1).max(200).nullable(),
});
export type DevicePinSetByAdmin = z.infer<typeof DevicePinSetByAdmin>;

export const DeviceRevoked = z.object({ revokedAt: Instant });
export type DeviceRevoked = z.infer<typeof DeviceRevoked>;

/** How many devices the one Prune button took out of service. */
export const DevicesPruned = z.object({ revoked: z.int().nonnegative() });
export type DevicesPruned = z.infer<typeof DevicesPruned>;

/**
 * How long a device may go unseen before Prune takes it. 30 days: long enough
 * that a shop closed for August keeps its tablets, short enough that the
 * reinstalls and warranty swaps of a year do not pile up unnoticed.
 */
export const DEVICE_PRUNE_UNSEEN_DAYS = 30;
